import axios from "axios";
import pDebounce from "p-debounce";

const KLEBER_BASE_URL =
  process.env.REACT_APP_ACQ_APP_KLEBER_DATA_TOOLS_BASE_URL;
const TOKEN_GENERATOR_BASE_URL =
  process.env.REACT_APP_ACQ_APP_ADDRESS_TOKEN_GENERATOR_BASE_URL;

const API_ERROR_TOKEN_EXPIRED = "TOKEN_EXPIRED";

export type ApiGetResponse = {
  key: string;
};

export const REQUEST_CHANNEL = {
  AU: "lf-com-au",
  NZ: "gf-co-nz",
};

export const getTokenMethodWrapper = (url, countryCode) =>
  axios.get(url, {
    headers: {
      "LFS-Request-Channel": REQUEST_CHANNEL[countryCode],
    },
  });

export const generateTemporaryToken = async (countryCode) =>
  getTokenMethodWrapper(`${TOKEN_GENERATOR_BASE_URL}/token`, countryCode).then(
    (token) => token.data as ApiGetResponse,
  );

const getDataToolsMethodWrapper = (url: string) =>
  axios.get(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

export type SearchAddress = {
  id: string;
  AddressLine: string;
  Country: string;
  CountryCode: string;
  Locality: string;
  Postcode: string;
  RecordId: string;
  State: string;
};

export type SearchAddressesReturn =
  | { data: SearchAddress[] }
  | { error: string };

export const searchAddresses = async (
  temporaryRequestKey,
  addressLineQuery,
  countryCode,
): Promise<SearchAddressesReturn> => {
  const encodedTemporaryRequestKey = encodeURIComponent(temporaryRequestKey);
  const params = new URLSearchParams({
    Method: "DataTools.Capture.Address.Predictive.AuNzPaf.SearchAddress",
    AddressLine: addressLineQuery.replace(" ", "+"),
    ResultLimit: "10",
    DisplayOnlyCountryCode: countryCode,
    SearchOption: "",
    RequestId: "",
    RequestKey: encodedTemporaryRequestKey,
    DepartmentCode: "",
    OutputFormat: "json",
  });

  const query = decodeURIComponent(params.toString());

  const response = await getDataToolsMethodWrapper(
    `${KLEBER_BASE_URL}/ProcessQueryStringRequest?${query}`,
  );

  if (
    response.data.DtResponse.ErrorMessage ===
    "Temporary Request Key has expired"
  ) {
    return { error: API_ERROR_TOKEN_EXPIRED };
  }
  const data = response.data.DtResponse.Result.map((d) => ({
    id: d.RecordId,
    ...d,
  }));

  return { data };
};

interface Params {
  addressLineQuery: string;
  countryCode: string;
  tempToken?: string;
  retry?: number;
}

const searchPlaces = async ({
  addressLineQuery,
  countryCode,
  tempToken,
  retry = 3,
}: Params): Promise<SearchAddressesReturn> => {
  try {
    if (retry === 0) {
      return { error: "Retry limit exceeds." };
    }

    let token: string | ApiGetResponse | undefined = tempToken;
    if (!token) {
      token = await generateTemporaryToken(countryCode);
    }

    const res = await searchAddresses(token, addressLineQuery, countryCode);
    if ("error" in res && res.error === API_ERROR_TOKEN_EXPIRED) {
      return await searchPlaces({
        addressLineQuery,
        countryCode,
        retry: retry - 1,
      });
    }
    return res;
  } catch (err) {
    console.error(err);
    if (retry) {
      return searchPlaces({ addressLineQuery, countryCode, retry: 1 });
    }
    return Promise.resolve({ error: "Something went wrong" });
  }
};

export const debouncedSearch = pDebounce(searchPlaces, 500);
